import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '../services/unifiedApiClient';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import BlogSubscription from '../components/BlogSubscription';
import '../styles/BlogContent.css';

const T = {
  paper:      '#F7EFD8',
  paperDeep:  '#EEE2BF',
  paperSoft:  '#FDF7E5',
  card:       '#FFFBEC',
  ink:        '#2E2109',
  ink2:       '#4A3510',
  inkMuted:   '#6E5A38',
  inkFaint:   '#A39068',
  rule:       '#D7C9A1',
  ruleStrong: '#BFAB79',
  orange:     '#EA7C1F',
  orangeDark: '#C25F0E',
  moss:       '#6B7A3E',
  mossDark:   '#4E5A29',
  mossSoft:   '#D7DCB9',
  capRed:     '#C14A33',
  capRedSoft: '#F2C7BA',
  roof:       '#C76A3B',
  roofSoft:   '#F2D1B3',
  r2: '6px', r3: '10px', r4: '14px', full: '999px',
  sh1: '0 1px 0 rgba(46,33,9,0.06), 0 2px 4px rgba(46,33,9,0.04)',
  sh2: '0 2px 0 rgba(46,33,9,0.04), 0 8px 16px -8px rgba(46,33,9,0.14)',
  sans: '"Nunito", ui-sans-serif, system-ui, -apple-system, sans-serif',
  hand: '"Caveat", "Nunito", cursive',
  mono: '"JetBrains Mono", ui-monospace, "SF Mono", Menlo, monospace',
};

// Gradient palettes for post image placeholders — cycles through a set
const IMAGE_GRADIENTS = [
  `linear-gradient(135deg, ${T.mossSoft} 0%, ${T.roofSoft} 100%)`,
  `linear-gradient(135deg, ${T.roofSoft} 0%, ${T.paperDeep} 100%)`,
  `linear-gradient(135deg, ${T.paperDeep} 0%, ${T.mossSoft} 100%)`,
  `linear-gradient(135deg, ${T.capRedSoft} 0%, ${T.roofSoft} 100%)`,
  `linear-gradient(135deg, ${T.mossSoft} 0%, ${T.capRedSoft} 100%)`,
];

const gradientFor = (idx) => IMAGE_GRADIENTS[idx % IMAGE_GRADIENTS.length];

const TagBadge = ({ children }) => (
  <span style={{
    background: T.orange,
    color: '#fff',
    fontSize: 11,
    fontWeight: 800,
    padding: '3px 9px',
    borderRadius: T.full,
    fontFamily: T.sans,
    letterSpacing: 0.2,
    display: 'inline-block',
  }}>
    {children}
  </span>
);

const SkeletonCard = ({ wide }) => (
  <div style={{
    background: T.card,
    borderRadius: T.r4,
    boxShadow: T.sh2,
    overflow: 'hidden',
    gridColumn: wide ? 'span 2' : undefined,
    border: `1.5px solid ${T.rule}`,
  }}>
    <div style={{
      height: wide ? 220 : 160,
      background: `linear-gradient(90deg, ${T.paperDeep} 25%, ${T.paper} 50%, ${T.paperDeep} 75%)`,
      backgroundSize: '200% 100%',
      animation: 'shimmer 1.4s infinite',
      borderBottom: `1px dashed ${T.rule}`,
    }} />
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ height: 12, background: T.paperDeep, borderRadius: T.r2, width: '40%' }} />
      <div style={{ height: 16, background: T.paperDeep, borderRadius: T.r2, width: '85%' }} />
      <div style={{ height: 13, background: T.paperDeep, borderRadius: T.r2, width: '70%' }} />
      <div style={{ height: 13, background: T.paperDeep, borderRadius: T.r2, width: '55%' }} />
    </div>
  </div>
);

const CottageButton = ({ children, onClick, variant = 'soft', size = 'md', style: extraStyle }) => {
  const [hovered, setHovered] = useState(false);

  const sizes = {
    sm: { fontSize: 12, padding: '7px 14px' },
    md: { fontSize: 14, padding: '10px 20px' },
  };

  const variants = {
    primary: {
      background: hovered ? T.orangeDark : T.orange,
      color: '#fff',
      border: 'none',
      boxShadow: hovered ? T.sh2 : T.sh1,
    },
    soft: {
      background: hovered ? T.paperDeep : T.paper,
      color: T.ink2,
      border: `1.5px solid ${T.rule}`,
      boxShadow: hovered ? T.sh1 : 'none',
    },
    ghost: {
      background: 'transparent',
      color: T.inkMuted,
      border: `1.5px dashed ${T.rule}`,
    },
    moss: {
      background: hovered ? T.mossDark : T.moss,
      color: '#fff',
      border: 'none',
      boxShadow: hovered ? T.sh2 : T.sh1,
    },
  };

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        fontFamily: T.sans,
        fontWeight: 800,
        borderRadius: T.full,
        cursor: 'pointer',
        transition: 'background 0.15s, box-shadow 0.15s',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        lineHeight: 1,
        ...sizes[size],
        ...variants[variant],
        ...extraStyle,
      }}
    >
      {children}
    </button>
  );
};

// Blog post listing card
const BlogPostCard = ({ post, index, onClick, wide }) => {
  const [hovered, setHovered] = useState(false);
  const imageHeight = wide ? 220 : 160;

  const dateStr = post.published_at || post.created_at
    ? new Date(post.published_at || post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '';

  const wordCount = post.content ? post.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0;
  const readTime = wordCount > 0 ? `${Math.max(1, Math.round(wordCount / 200))} min read` : null;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: T.card,
        borderRadius: T.r4,
        boxShadow: hovered ? '0 4px 0 rgba(46,33,9,0.06), 0 16px 32px -12px rgba(46,33,9,0.18)' : T.sh2,
        border: `1.5px solid ${hovered ? T.ruleStrong : T.rule}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, border-color 0.2s, transform 0.15s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        gridColumn: wide ? 'span 2' : undefined,
      }}
    >
      {/* Image / gradient area */}
      <div style={{
        height: imageHeight,
        background: post.featured_image ? undefined : gradientFor(index),
        borderBottom: `1px dashed ${T.rule}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        flexShrink: 0,
        position: 'relative',
      }}>
        {post.featured_image ? (
          <img
            src={post.featured_image}
            alt={post.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <div style={{
            fontFamily: T.hand,
            fontSize: wide ? 28 : 22,
            color: T.inkFaint,
            opacity: 0.6,
            userSelect: 'none',
            textAlign: 'center',
            padding: '0 20px',
          }}>
            {post.title}
          </div>
        )}
      </div>

      {/* Body */}
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
        {/* Meta row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {post.tags && post.tags.length > 0 && (
            <TagBadge>{post.tags[0]}</TagBadge>
          )}
          {dateStr && (
            <span style={{ fontFamily: T.mono, fontSize: 11, color: T.inkFaint }}>{dateStr}</span>
          )}
          {readTime && (
            <span style={{ fontFamily: T.mono, fontSize: 11, color: T.inkFaint }}>· {readTime}</span>
          )}
        </div>

        {/* Title */}
        <div style={{
          fontSize: 17,
          fontWeight: 800,
          color: T.ink,
          fontFamily: T.sans,
          lineHeight: 1.25,
        }}>
          {post.title}
        </div>

        {/* Excerpt / deck */}
        {(post.excerpt || post.content) && (
          <div style={{
            fontSize: 13,
            color: T.ink2,
            fontFamily: T.sans,
            lineHeight: 1.55,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
            {post.excerpt || post.content.replace(/<[^>]*>/g, '').substring(0, 180) + '…'}
          </div>
        )}

        {/* Remaining tags */}
        {post.tags && post.tags.length > 1 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            {post.tags.slice(1, 4).map(tag => (
              <TagBadge key={tag}>{tag}</TagBadge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Blog post detail view
const BlogPostDetail = ({ post, onBack }) => (
  <div style={{
    maxWidth: 800,
    margin: '0 auto',
    padding: '36px 40px',
    fontFamily: T.sans,
  }}>
    <SEO
      title={post.title}
      description={post.excerpt || (post.content ? post.content.replace(/<[^>]*>/g, '').substring(0, 160) : '')}
      image={post.featured_image}
      article={true}
      author={post.author_name}
      publishedTime={post.published_at || post.created_at}
      tags={post.tags || []}
    />

    <CottageButton variant="soft" size="sm" onClick={onBack} style={{ marginBottom: 28 }}>
      ← Back to Field Notes
    </CottageButton>

    <div style={{
      background: T.card,
      border: `1.5px solid ${T.rule}`,
      borderRadius: T.r4,
      boxShadow: T.sh2,
      overflow: 'hidden',
    }}>
      {/* Hero image or gradient */}
      <div style={{
        height: 280,
        background: post.featured_image ? undefined : gradientFor(0),
        borderBottom: `1px dashed ${T.rule}`,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        {post.featured_image ? (
          <img src={post.featured_image} alt={post.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ fontFamily: T.hand, fontSize: 32, color: T.inkFaint, opacity: 0.5 }}>
            {post.title}
          </div>
        )}
      </div>

      <div style={{ padding: 32 }}>
        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {post.tags.map(tag => <TagBadge key={tag}>{tag}</TagBadge>)}
          </div>
        )}

        {/* Title */}
        <h1 style={{
          fontSize: 30,
          fontWeight: 800,
          color: T.ink,
          fontFamily: T.sans,
          margin: '0 0 12px',
          lineHeight: 1.2,
          letterSpacing: -0.5,
        }}>
          {post.title}
        </h1>

        {/* Meta */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 28, color: T.inkMuted, fontSize: 13, fontFamily: T.mono, flexWrap: 'wrap' }}>
          <span>By {post.author_name}</span>
          <span>·</span>
          <span>{new Date(post.published_at || post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          {post.view_count != null && (
            <>
              <span>·</span>
              <span>{post.view_count} views</span>
            </>
          )}
        </div>

        {/* Content */}
        <div
          className="blog-content"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || '') }}
          style={{
            fontSize: 15,
            lineHeight: 1.8,
            color: T.ink,
            fontFamily: T.sans,
          }}
        />

        <div style={{ marginTop: 40, paddingTop: 28, borderTop: `1px dashed ${T.rule}` }}>
          <BlogSubscription title="Enjoyed this article?" variant="inline" />
        </div>
      </div>
    </div>
  </div>
);

// Search bar
const SearchBar = ({ value, onChange, onSearch }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display: 'flex', gap: 8, flex: 1, maxWidth: 380 }}>
      <input
        type="text"
        placeholder="Search field notes…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && onSearch()}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{
          flex: 1,
          padding: '9px 14px',
          border: `1.5px solid ${focused ? T.ruleStrong : T.rule}`,
          borderRadius: T.full,
          background: T.paperSoft,
          color: T.ink,
          fontSize: 14,
          fontFamily: T.sans,
          fontWeight: 600,
          outline: 'none',
          boxShadow: focused ? `0 0 0 3px ${T.orange}22` : 'none',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
      />
      <CottageButton variant="soft" size="sm" onClick={onSearch}>Search</CottageButton>
    </div>
  );
};

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedPost, setSelectedPost] = useState(null);
  const [mySubscription, setMySubscription] = useState(null);

  const { showToast, ToastComponent } = useToast();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();

  useEffect(() => {
    if (slug) {
      fetchPostBySlug(slug);
    } else {
      fetchPosts();
    }
    if (user) {
      fetchMySubscription();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, user]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      const response = await apiClient.get('/blog/posts', { params });
      setPosts(response.posts || []);
    } catch (error) {
      showToast('Failed to fetch blog posts', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostBySlug = async (postSlug) => {
    try {
      setLoading(true);
      const post = await apiClient.get(`/blog/posts/${postSlug}`);
      setSelectedPost(post);
    } catch (error) {
      showToast('Failed to fetch blog post', 'error');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchMySubscription = async () => {
    try {
      const subscription = await apiClient.get('/blog/my-subscription');
      setMySubscription(subscription);
    } catch (error) {
      // User doesn't have a subscription
    }
  };

  const handleSearch = () => {
    fetchPosts();
  };

  const handlePostClick = (post) => {
    navigate(`/blog/${post.slug}`);
  };

  const handleBackToBlog = () => {
    navigate('/blog');
    setSelectedPost(null);
  };

  const handleToggleSubscription = async () => {
    if (!user) {
      showToast('Please sign in to subscribe', 'info');
      return;
    }
    try {
      if (mySubscription) {
        if (window.confirm('Are you sure you want to unsubscribe from blog notifications?')) {
          await apiClient.delete('/blog/my-subscription');
          showToast('Successfully unsubscribed', 'success');
          setMySubscription(null);
        }
      } else {
        await apiClient.post('/blog/subscribe', {
          email: user.email,
          frequency: 'weekly',
          categories: [],
        });
        showToast("Successfully subscribed! You'll receive weekly updates.", 'success');
        fetchMySubscription();
      }
    } catch (error) {
      showToast(error.response?.data?.detail || 'Failed to update subscription', 'error');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: '36px 40px', fontFamily: T.sans }}>
        <SEO title="Blog" description="Read the latest insights from the Cahoots team." />
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontFamily: T.hand, fontSize: 28, color: T.orange, transform: 'rotate(-1deg)', display: 'inline-block' }}>
            field notes —
          </div>
          <h1 style={{ fontSize: 44, fontWeight: 800, color: T.ink, margin: '6px 0 6px', letterSpacing: -1, lineHeight: 1.05, fontFamily: T.sans }}>
            Things we learned building Cahoots.
          </h1>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 18 }}>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <SkeletonCard key={i} wide={i === 0} />
          ))}
        </div>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>
      </div>
    );
  }

  // Detail view
  if (selectedPost) {
    return <BlogPostDetail post={selectedPost} onBack={handleBackToBlog} />;
  }

  return (
    <div style={{
      padding: '36px 40px',
      minHeight: '100vh',
      background: T.paper,
      fontFamily: T.sans,
    }}>
      <SEO
        title="Blog"
        description="Read the latest insights, updates, and tutorials from the Cahoots team about AI-powered product planning and software development."
      />

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{
          fontFamily: T.hand,
          fontSize: 28,
          color: T.orange,
          transform: 'rotate(-1deg)',
          display: 'inline-block',
          lineHeight: 1,
        }}>
          field notes —
        </div>
        <h1 style={{
          fontSize: 44,
          fontWeight: 800,
          color: T.ink,
          margin: '6px 0 6px',
          letterSpacing: -1,
          lineHeight: 1.05,
          fontFamily: T.sans,
        }}>
          Things we learned building Cahoots.
        </h1>
      </div>

      {/* Search + Subscribe row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        marginBottom: 26,
        flexWrap: 'wrap',
      }}>
        <SearchBar value={search} onChange={setSearch} onSearch={handleSearch} />
        <CottageButton
          variant={mySubscription ? 'soft' : 'moss'}
          size="sm"
          onClick={handleToggleSubscription}
        >
          {mySubscription ? '✓ Subscribed' : '+ Subscribe'}
        </CottageButton>
      </div>

      {/* Post grid */}
      {posts.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: T.card,
          border: `1.5px dashed ${T.rule}`,
          borderRadius: T.r4,
        }}>
          <div style={{ fontFamily: T.hand, fontSize: 36, color: T.inkFaint, marginBottom: 10 }}>nothing here yet —</div>
          <div style={{ fontSize: 14, color: T.inkMuted, fontFamily: T.sans }}>Check back soon for field notes from the team.</div>
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 18,
        }}>
          {posts.map((post, idx) => (
            <BlogPostCard
              key={post.id}
              post={post}
              index={idx}
              wide={idx === 0}
              onClick={() => handlePostClick(post)}
            />
          ))}
        </div>
      )}

      <ToastComponent />
    </div>
  );
};

export default BlogPage;
